// The site's motion, started once per page load from App.
//
// 1. Reveal: blocks ease up and fade in the first time they come into view,
//    in a short stagger, then are left alone; scrolling back never replays
//    them. Which blocks is REVEAL below. The CSS that hides them before they
//    arrive lives under `.motion` in global.css and must list the same
//    selectors.
// 2. Accordions: FAQ answers open and close with a height animation.
// 3. The header gains a soft shadow once the page has scrolled.
//
// public/index.html adds the `motion` class to <html> before the page paints
// (skipped for reduced motion), and takes it away again if this script hasn't
// started within 3 s, so content is never left hidden.

declare global {
  interface Window {
    __hetheraMotion?: boolean;
  }
}

// Groups, not their items: a row of cards arrives as one. Long reading
// pages (the legal ones) don't move at all.
export const REVEAL = [
  '.hero__copy > :not(h1)',
  '.hero h1',
  '.hero__sticky',
  '.providers__inner',
  '.section-head',
  '.card-row',
  '.sec-grid',
  '.feature-list',
  '.faq',
  '.report-mock',
  '.split__aside',
  '.company',
  '.cta',
  '.shortcuts',
  '.showcase__copy',
  '.info-hero__copy > *',
  '.info-hero__aside',
  '.info-section__head',
  '.principles',
  '.tile-grid',
  '.topic-grid',
  '.keyword-list',
  '.office',
  '.safety-note',
  '.paid__card',
].join(',');

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const STAGGER_MS = 60;
const MAX_STAGGER = 4;
const SETTLE_MS = 900;

export function startMotion(): () => void {
  const root = document.documentElement;
  const cleanups = [watchHeader()];
  if (root.classList.contains('motion')) {
    window.__hetheraMotion = true;
    cleanups.push(reveal(), accordions());
  }
  return () => cleanups.forEach((stop) => stop());
}

function reveal(): () => void {
  const items = Array.from(document.querySelectorAll<HTMLElement>(REVEAL));
  const done = (el: HTMLElement) => {
    el.classList.remove('rv-in');
    el.classList.add('rv-done');
    el.style.removeProperty('--rv-delay');
  };
  // Anything already scrolled past (a reload halfway down, an anchor link)
  // just appears; only what's ahead of the reader animates.
  items.forEach((el) => {
    if (el.getBoundingClientRect().bottom < 0) done(el);
  });

  const timers: ReturnType<typeof setTimeout>[] = [];
  const observer = new IntersectionObserver(
    (entries) => {
      entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left)
        .forEach((entry, i) => {
          const el = entry.target as HTMLElement;
          observer.unobserve(el);
          const delay = Math.min(i, MAX_STAGGER) * STAGGER_MS;
          el.style.setProperty('--rv-delay', `${delay}ms`);
          el.classList.add('rv-in');
          timers.push(setTimeout(() => done(el), delay + SETTLE_MS));
        });
    },
    { rootMargin: '0px 0px 6% 0px', threshold: 0 },
  );
  items.filter((el) => !el.classList.contains('rv-done')).forEach((el) => observer.observe(el));

  // Only finish what's mid-animation. Blocks still waiting are left as they
  // are, so a restart (React StrictMode runs effects twice in development)
  // picks them up again instead of finding everything already "done".
  return () => {
    observer.disconnect();
    timers.forEach(clearTimeout);
    items.filter((el) => el.classList.contains('rv-in')).forEach(done);
  };
}

// <details> can't animate its own height, so the summary click is taken over:
// the element is held at its current height, toggled, and animated to the
// new one. The answer fades in a beat after the panel starts to open.
function accordions(): () => void {
  const offs: (() => void)[] = [];
  document.querySelectorAll<HTMLDetailsElement>('.faq details').forEach((details) => {
    const summary = details.querySelector('summary');
    if (!summary) return;
    let running: Animation | null = null;

    const onClick = (event: MouseEvent) => {
      event.preventDefault();
      if (running) return;
      const body = Array.from(details.children).filter((c) => c !== summary) as HTMLElement[];
      const opening = !details.open;
      const start = details.offsetHeight;
      details.open = !details.open;
      const end = details.offsetHeight;
      details.open = true; // stay open while closing so the answer stays visible
      details.classList.toggle('is-closing', !opening);
      details.style.overflow = 'hidden';

      running = details.animate([{ height: `${start}px` }, { height: `${end}px` }], {
        duration: opening ? 380 : 300,
        easing: EASE,
      });
      // Closing, the fade must hold at 0 until the panel is shut (fill
      // 'forwards'), or the answer pops back in at the top for the rest of
      // the collapse.
      const fades = body.map((el) =>
        el.animate(
          opening
            ? [
                { opacity: 0 },
                { opacity: 1 },
              ]
            : [{ opacity: 1 }, { opacity: 0 }],
          opening
            ? { duration: 320, delay: 60, easing: EASE, fill: 'backwards' }
            : { duration: 200, easing: 'ease-out', fill: 'forwards' },
        ),
      );
      running.onfinish = () => {
        details.open = opening;
        fades.forEach((fade) => fade.cancel());
        details.classList.remove('is-closing');
        details.style.overflow = '';
        running = null;
      };
    };
    summary.addEventListener('click', onClick);
    offs.push(() => summary.removeEventListener('click', onClick));
  });
  return () => offs.forEach((off) => off());
}

function watchHeader(): () => void {
  const header = document.querySelector('.site-header');
  if (!header) return () => {};
  const update = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
  update();
  window.addEventListener('scroll', update, { passive: true });
  return () => window.removeEventListener('scroll', update);
}
